import React, { useState } from 'react';
import { 
  ClipboardCheck, Shield, Microscope, PackageCheck, Factory, 
  FileCheck, ChevronRight, MapPin, Phone, Mail, ChevronDown,
  Building2, Car, Smartphone, Shirt, Coffee,
  Clock 
} from 'lucide-react';
import Breadcrumbs from '../Navigation/Breadcrumbs';
import RequestForm from './RequestForm';
import { useLanguage } from '../../contexts/LanguageContext';

function QualityInspectionPage() {
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Gradient Background */}
      <section className="pt-20 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-[#1B3BA0]/50 mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#1B3BA0]/20"></div>
        </div>
        <div className="max-w-[980px] mx-auto relative">
          <div className="mb-12">
            <Breadcrumbs />
          </div>
          <div className="max-w-[780px]">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
              <span className="text-white block mb-2">
                {language === 'ru' ? 'Инспекционный контроль' : 
                 language === 'en' ? 'Quality Inspection' : 
                 '质量检验'}
              </span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-[#99BBFF]">
                {language === 'ru' ? 'качества товаров' : 
                 language === 'en' ? 'Control Services' : 
                 '控制服务'}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed">
              {language === 'ru' 
                ? 'Профессиональная проверка качества и соответствия продукции на всех этапах производства'
                : language === 'en' 
                ? 'Professional quality control and product compliance at all stages of production'
                : '生产各阶段的专业质量控制和产品合规性'}
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid with Elevated Cards */}
      <section className="py-24 px-6 bg-white relative z-10 -mt-20">
        <div className="max-w-[980px] mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl border border-gray-100">
            <h2 className="text-3xl font-bold text-[#1B3BA0] mb-6">
              {language === 'ru' ? 'Наши услуги' : 
               language === 'en' ? 'Our Services' : 
               '我们的服务'}
            </h2>
            <p className="text-lg text-gray-600 mb-12 leading-relaxed">
              {language === 'ru' 
                ? 'Предлагаем полный спектр услуг по контролю качества продукции на всех этапах производства и отгрузки.'
                : language === 'en'
                ? 'We offer a full range of product quality control services at all stages of production and shipment.'
                : '我们在生产和装运的所有阶段提供全方位的产品质量控制服务。'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: <Factory className="w-12 h-12 text-[#1B3BA0]" />,
                  title: language === 'ru' ? 'Выезд на производство' : 
                          language === 'en' ? 'Factory Visit' : 
                          '工厂访问',
                  description: language === 'ru' ? 'Проверка соответствия продукции требованиям заказчика и условиям контракта' : 
                               language === 'en' ? 'Checking the compliance of products with customer requirements and contract terms' : 
                               '检查产品是否符合客户要求和合同条款'
                },
                {
                  icon: <PackageCheck className="w-12 h-12 text-[#1B3BA0]" />,
                  title: language === 'ru' ? 'Предотгрузочный контроль' : 
                          language === 'en' ? 'Pre-shipment Inspection' : 
                          '装运前检验',
                  description: language === 'ru' ? 'Проверка внешнего вида, функциональности, комплектности и упаковки' : 
                               language === 'en' ? 'Checking appearance, functionality, completeness and packaging' : 
                               '检查外观、功能、完整性和包装'
                },
                {
                  icon: <FileCheck className="w-12 h-12 text-[#1B3BA0]" />,
                  title: language === 'ru' ? 'Подробные отчёты' : 
                          language === 'en' ? 'Detailed Reports' : 
                          '详细报告',
                  description: language === 'ru' ? 'Составление детальных отчётов с фото и видеоматериалами' : 
                               language === 'en' ? 'Preparation of detailed reports with photos and videos' : 
                               '准备带有照片和视频的详细报告'
                }
              ].map((service, index) => (
                <div key={index} className="bg-[#1B3BA0]/5 rounded-2xl p-8 hover:bg-[#1B3BA0]/10 transition-all duration-300 transform hover:-translate-y-1">
                  <div className="mb-6">{service.icon}</div>
                  <h3 className="text-xl font-bold text-[#1B3BA0] mb-4">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section with Gradient Background */}
      <section className="py-32 px-6 bg-gradient-to-b from-white to-[#1B3BA0]/5">
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#1B3BA0] mb-16">
            {language === 'ru' ? 'Преимущества инспекционного контроля' : 
             language === 'en' ? 'Benefits of Inspection Control' : 
             '检验控制的好处'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: <Shield className="w-12 h-12 text-[#1B3BA0]" />,
                title: language === 'ru' ? 'Снижение рисков' : 
                        language === 'en' ? 'Risk Reduction' : 
                        '降低风险',
                description: language === 'ru' ? 'Минимизация вероятности получения некачественного товара' : 
                             language === 'en' ? 'Minimizing the probability of receiving poor quality goods' : 
                             '最大限度地减少收到劣质商品的可能性'
              },
              {
                icon: <ClipboardCheck className="w-12 h-12 text-[#1B3BA0]" />,
                title: language === 'ru' ? 'Гарантия соответствия' : 
                        language === 'en' ? 'Compliance Guarantee' : 
                        '合规保证',
                description: language === 'ru' ? 'Уверенность в соответствии продукции требованиям и ожиданиям' : 
                             language === 'en' ? 'Confidence in product compliance with requirements and expectations' : 
                             '对产品符合要求和期望的信心'
              },
              {
                icon: <Microscope className="w-12 h-12 text-[#1B3BA0]" />,
                title: language === 'ru' ? 'Детальная проверка' : 
                        language === 'en' ? 'Detailed Inspection' : 
                        '详细检查',
                description: language === 'ru' ? 'Тщательный контроль каждого аспекта производства и качества' : 
                             language === 'en' ? 'Thorough control of every aspect of production and quality' : 
                             '对生产和质量的每个方面进行彻底控制'
              },
              {
                icon: <Clock className="w-12 h-12 text-[#1B3BA0]" />,
                title: language === 'ru' ? 'Экономия ресурсов' : 
                        language === 'en' ? 'Resource Saving' : 
                        '资源节约',
                description: language === 'ru' ? 'Своевременное выявление и устранение несоответствий' : 
                             language === 'en' ? 'Timely identification and elimination of discrepancies' : 
                             '及时识别和消除差异'
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="mb-6">{benefit.icon}</div>
                <h3 className="text-xl font-bold text-[#1B3BA0] mb-4">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#1B3BA0] mb-16">
            {language === 'ru' ? 'Отрасли' : 
             language === 'en' ? 'Industries' : 
             '行业'}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            {[
              { 
                icon: <Building2 className="w-8 h-8" />, 
                name: language === 'ru' ? 'Бытовые товары' : 
                      language === 'en' ? 'Household Goods' : 
                      '家居用品' 
              },
              { 
                icon: <Smartphone className="w-8 h-8" />, 
                name: language === 'ru' ? 'Электроника' : 
                      language === 'en' ? 'Electronics' : 
                      '电子产品' 
              },
              { 
                icon: <Shirt className="w-8 h-8" />, 
                name: language === 'ru' ? 'Текстиль' : 
                      language === 'en' ? 'Textiles' : 
                      '纺织品' 
              },
              { 
                icon: <Car className="w-8 h-8" />, 
                name: language === 'ru' ? 'Автозапчасти' : 
                      language === 'en' ? 'Auto Parts' : 
                      '汽车零部件' 
              },
              { 
                icon: <Coffee className="w-8 h-8" />, 
                name: language === 'ru' ? 'Продукты питания' : 
                      language === 'en' ? 'Food Products' : 
                      '食品' 
              }
            ].map((industry, index) => (
              <div key={index} className="text-center">
                <div className="bg-[#1B3BA0]/5 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center text-[#1B3BA0]">
                  {industry.icon}
                </div>
                <p className="text-gray-700 font-medium">{industry.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-32 px-6 bg-[#1B3BA0]">
        <div className="max-w-[780px] mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {language === 'ru' ? 'Заказать инспекцию' : 
             language === 'en' ? 'Order Inspection' : 
             '订购检验'}
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            {language === 'ru' 
              ? 'Оставьте заявку, и мы свяжемся с вами для обсуждения деталей проверки'
              : language === 'en' 
              ? 'Leave a request, and we will contact you to discuss the details of the inspection'
              : '留下请求，我们将联系您讨论检验的详细信息'}
          </p>
          <RequestForm source={language === 'ru' ? "Инспекционный контроль" : language === 'en' ? "Quality Inspection" : "质量检验"} />
        </div>
      </section>
    </div>
  );
}

export default QualityInspectionPage;